import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2, Building2, Check, ShieldCheck, X, MapPin } from "lucide-react";
import api from "../lib/api";
import { useToast } from "../hooks/use-toast";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";

const emailValid = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || "").trim());
const phoneValid = (v) => /^\d{6,15}$/.test(String(v || "").replace(/\D/g, ""));

const blankEntry = (dial = "+60") => ({
  state: "", state_id: "", city: "", address1: "", address2: "",
  contact_person: "", contact_email: "", contact_email_verified: false,
  tel_dial: dial, tel: "", mobile_dial: dial, mobile: "", mobile_verified: false,
  _emailSent: false, _emailCode: "", _emailDemo: "", _mobileSent: false, _mobileCode: "", _mobileDemo: "",
});

export default function Offices() {
  const { toast } = useToast();
  const [rows, setRows] = useState([]);
  const [countries, setCountries] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [dial, setDial] = useState([]);
  const [states, setStates] = useState([]);
  const [citiesByIdx, setCitiesByIdx] = useState({});
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ country_id: "", country_code: "", company_id: "", entries: [blankEntry()] });

  const load = () => {
    setLoading(true);
    api.get("/masters/offices").then((r) => setRows(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    api.get("/masters/countries").then((r) => setCountries(r.data)).catch(() => {});
    api.get("/masters/companies").then((r) => setCompanies(r.data)).catch(() => {});
    api.get("/auth/dial-codes").then((r) => setDial(r.data)).catch(() => {});
  }, []);

  const countryName = (id) => countries.find((c) => c.id === id)?.name || "—";
  const companyName = (id) => companies.find((c) => c.id === id)?.name || "—";

  const onCountry = (country_id) => {
    const c = countries.find((x) => x.id === country_id);
    const code = c?.code || "";
    const d = c?.dial_code || "+60";
    setForm((f) => ({
      ...f, country_id, country_code: code,
      entries: f.entries.map((e) => ({ ...e, tel_dial: e.mobile_verified ? e.tel_dial : d, mobile_dial: e.mobile_verified ? e.mobile_dial : d })),
    }));
    setStates([]);
    if (code) api.get(`/geo/states?country=${code}`).then((r) => setStates(r.data)).catch(() => setStates([]));
  };

  const setEntry = (idx, patch) => setForm((f) => ({ ...f, entries: f.entries.map((e, i) => i === idx ? { ...e, ...patch } : e) }));

  const onState = (idx, stateName) => {
    const st = states.find((s) => s.name === stateName);
    setEntry(idx, { state: stateName, state_id: st?.id || "", city: "" });
    if (st?.id) api.get(`/geo/cities?state_id=${st.id}`).then((r) => setCitiesByIdx((m) => ({ ...m, [idx]: r.data }))).catch(() => {});
  };

  const openCreate = () => {
    setForm({ country_id: "", country_code: "", company_id: "", entries: [blankEntry()] });
    setStates([]); setCitiesByIdx({}); setOpen(true);
  };

  const addEntry = () => setForm((f) => ({ ...f, entries: [...f.entries, blankEntry(countries.find((c) => c.id === f.country_id)?.dial_code || "+60")] }));
  const removeEntry = (idx) => setForm((f) => ({ ...f, entries: f.entries.filter((_, i) => i !== idx) }));

  // --- OTP verification for contact email / mobile ---
  const sendEmailOtp = async (idx, e) => {
    if (!emailValid(e.contact_email)) { toast({ title: "Enter a valid email first", variant: "destructive" }); return; }
    try {
      const { data } = await api.post("/masters/verify-contact/send", { channel: "email", email: e.contact_email });
      setEntry(idx, { _emailSent: true, _emailDemo: data.demo || "" });
      toast({ title: data.sent ? "Code emailed" : "Code generated (demo)", description: data.demo ? `Demo code: ${data.demo}` : "Check the inbox." });
    } catch (err) { toast({ title: "Could not send code", description: err?.response?.data?.detail || "", variant: "destructive" }); }
  };
  const checkEmailOtp = async (idx, e) => {
    try {
      await api.post("/masters/verify-contact/check", { channel: "email", email: e.contact_email, code: e._emailCode });
      setEntry(idx, { contact_email_verified: true, _emailSent: false });
      toast({ title: "Email verified" });
    } catch (err) { toast({ title: "Invalid code", description: err?.response?.data?.detail || "", variant: "destructive" }); }
  };
  const sendMobileOtp = async (idx, e) => {
    if (!phoneValid(e.mobile)) { toast({ title: "Enter a valid mobile first", variant: "destructive" }); return; }
    try {
      const { data } = await api.post("/masters/verify-contact/send", { channel: "mobile", dial: e.mobile_dial, phone: e.mobile });
      setEntry(idx, { _mobileSent: true, _mobileDemo: data.demo || "" });
      toast({ title: data.sent ? "Code sent by SMS" : "Code generated (demo)", description: data.demo ? `Demo code: ${data.demo}` : "Check your phone." });
    } catch (err) { toast({ title: "Could not send code", description: err?.response?.data?.detail || "", variant: "destructive" }); }
  };
  const checkMobileOtp = async (idx, e) => {
    try {
      await api.post("/masters/verify-contact/check", { channel: "mobile", dial: e.mobile_dial, phone: e.mobile, code: e._mobileCode });
      setEntry(idx, { mobile_verified: true, _mobileSent: false });
      toast({ title: "Mobile verified" });
    } catch (err) { toast({ title: "Invalid code", description: err?.response?.data?.detail || "", variant: "destructive" }); }
  };

  const save = async () => {
    if (!form.country_id) { toast({ title: "Select a country", variant: "destructive" }); return; }
    for (let i = 0; i < form.entries.length; i++) {
      const e = form.entries[i];
      const miss = [];
      if (!e.state) miss.push("state");
      if (!e.city) miss.push("city");
      if (!e.address1) miss.push("address 1");
      if (!e.address2) miss.push("address 2");
      if (!e.contact_person) miss.push("contact person");
      if (!e.contact_email) miss.push("contact email");
      if (!e.mobile) miss.push("mobile");
      if (miss.length) { toast({ title: `Office #${i + 1}: fill ${miss.join(", ")}`, variant: "destructive" }); return; }
      if (!e.contact_email_verified) { toast({ title: `Office #${i + 1}: verify the contact email`, variant: "destructive" }); return; }
      if (!e.mobile_verified) { toast({ title: `Office #${i + 1}: verify the mobile number`, variant: "destructive" }); return; }
    }
    setSaving(true);
    try {
      await api.post("/masters/offices/bulk", {
        country_id: form.country_id, company_id: form.company_id,
        offices: form.entries.map((e) => ({
          state: e.state, city: e.city, address1: e.address1, address2: e.address2,
          contact_person: e.contact_person, contact_email: e.contact_email, contact_email_verified: e.contact_email_verified,
          tel_dial: e.tel_dial, tel: e.tel, mobile_dial: e.mobile_dial, mobile: e.mobile, mobile_verified: e.mobile_verified,
        })),
      });
      toast({ title: `${form.entries.length} office(s) added` });
      setOpen(false); load();
    } catch (err) { toast({ title: "Save failed", description: err?.response?.data?.detail || "", variant: "destructive" }); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this office?")) return;
    await api.delete(`/masters/offices/${id}`); load();
  };

  return (
    <div className="space-y-6" data-testid="offices-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-[#012a1e]">Offices</h1>
          <p className="text-slate-500 text-sm">Branch offices per country &amp; company — named by state, auto-numbered when repeated.</p>
        </div>
        <Button onClick={openCreate} className="bg-[#004030] hover:bg-[#013d2b] text-white rounded-full" data-testid="add-office-btn"><Plus size={16} className="mr-1" /> Add Office</Button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        {loading ? <div className="p-10 text-center text-slate-400"><Loader2 className="animate-spin mx-auto" /></div>
          : rows.length === 0 ? <div className="p-10 text-center text-slate-400">No offices yet.</div>
          : (
            <table className="w-full text-sm">
              <thead className="bg-[#f0f6f3] text-[#004030]">
                <tr><th className="text-left px-4 py-3">Office</th><th className="text-left px-4 py-3">Country</th><th className="text-left px-4 py-3">Company</th><th className="text-left px-4 py-3">City</th><th className="text-left px-4 py-3">Contact</th><th className="px-4 py-3"></th></tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-t border-slate-50" data-testid={`office-row-${r.id}`}>
                    <td className="px-4 py-3 font-medium text-[#012a1e]">{r.name || r.state}</td>
                    <td className="px-4 py-3">{countryName(r.country_id)}</td>
                    <td className="px-4 py-3">{companyName(r.company_id)}</td>
                    <td className="px-4 py-3">{r.city || "—"}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">{r.contact_person || "—"}
                        {r.contact_email_verified && <ShieldCheck size={13} className="text-green-600" title="Email verified" />}
                        {r.mobile_verified && <ShieldCheck size={13} className="text-blue-600" title="Mobile verified" />}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right"><button onClick={() => del(r.id)} className="text-slate-400 hover:text-red-600" data-testid={`office-del-${r.id}`}><Trash2 size={15} /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="office-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl">Add Office</DialogTitle></DialogHeader>
          <div className="space-y-4 py-1">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Country <span className="text-red-500">*</span></Label>
                <Select value={form.country_id} onValueChange={onCountry}>
                  <SelectTrigger className="h-11" data-testid="office-country"><SelectValue placeholder="Select country" /></SelectTrigger>
                  <SelectContent>{countries.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Company <span className="text-red-500">*</span></Label>
                <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                  <SelectTrigger className="h-11" data-testid="office-company"><SelectValue placeholder="Select company" /></SelectTrigger>
                  <SelectContent>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            {form.entries.map((e, idx) => (
              <div key={idx} className="rounded-xl border border-slate-200 p-4 space-y-3 relative" data-testid={`office-entry-${idx}`}>
                <div className="flex items-center justify-between">
                  <p className="text-sm font-bold text-[#004030] flex items-center gap-1.5"><Building2 size={15} /> Office {idx + 1}{e.state ? ` — ${e.state}` : ""}</p>
                  {form.entries.length > 1 && <button onClick={() => removeEntry(idx)} className="text-slate-400 hover:text-red-600" data-testid={`office-remove-${idx}`}><X size={16} /></button>}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label>State / County <span className="text-red-500">*</span></Label>
                    {states.length ? (
                      <Select value={e.state} onValueChange={(v) => onState(idx, v)}>
                        <SelectTrigger className="h-11" data-testid={`office-state-${idx}`}><SelectValue placeholder="Select state" /></SelectTrigger>
                        <SelectContent>{states.map((s) => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}</SelectContent>
                      </Select>
                    ) : <Input value={e.state} onChange={(ev) => setEntry(idx, { state: ev.target.value })} placeholder="State name" className="h-11" data-testid={`office-state-${idx}`} />}
                    <p className="text-[11px] text-slate-400">Office name = state (auto-numbered if repeated)</p>
                  </div>
                  <div className="space-y-1">
                    <Label>City <span className="text-red-500">*</span></Label>
                    {(citiesByIdx[idx] || []).length ? (
                      <Select value={e.city} onValueChange={(v) => setEntry(idx, { city: v })}>
                        <SelectTrigger className="h-11" data-testid={`office-city-${idx}`}><SelectValue placeholder="Select city" /></SelectTrigger>
                        <SelectContent>{(citiesByIdx[idx] || []).map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                      </Select>
                    ) : <Input value={e.city} onChange={(ev) => setEntry(idx, { city: ev.target.value })} placeholder="City" className="h-11" data-testid={`office-city-${idx}`} />}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1"><Label>Address line 1 <span className="text-red-500">*</span></Label><Input value={e.address1} onChange={(ev) => setEntry(idx, { address1: ev.target.value })} className="h-11" data-testid={`office-addr1-${idx}`} /></div>
                  <div className="space-y-1"><Label>Address line 2 <span className="text-red-500">*</span></Label><Input value={e.address2} onChange={(ev) => setEntry(idx, { address2: ev.target.value })} className="h-11" data-testid={`office-addr2-${idx}`} /></div>
                </div>

                <div className="border-t border-slate-100 pt-3 space-y-3">
                  <div className="space-y-1"><Label>Contact person <span className="text-red-500">*</span></Label><Input value={e.contact_person} onChange={(ev) => setEntry(idx, { contact_person: ev.target.value })} className="h-11" data-testid={`office-contact-${idx}`} /></div>

                  <div className="space-y-1">
                    <Label>Contact email <span className="text-red-500">*</span> {e.contact_email_verified && <span className="text-green-600 text-xs">✓ verified</span>}</Label>
                    <div className="flex gap-2">
                      <Input type="email" value={e.contact_email} disabled={e.contact_email_verified} onChange={(ev) => setEntry(idx, { contact_email: ev.target.value, contact_email_verified: false })} className={`h-11 flex-1 ${e.contact_email && !emailValid(e.contact_email) ? "border-red-400" : ""}`} data-testid={`office-email-${idx}`} />
                      {!e.contact_email_verified && <Button type="button" variant="outline" className="h-11 rounded-full whitespace-nowrap" onClick={() => sendEmailOtp(idx, e)} data-testid={`office-email-verify-${idx}`}>Verify</Button>}
                    </div>
                    {e._emailSent && !e.contact_email_verified && (
                      <div className="flex gap-2 mt-1">
                        <Input value={e._emailCode} onChange={(ev) => setEntry(idx, { _emailCode: ev.target.value })} placeholder={e._emailDemo ? `code (demo ${e._emailDemo})` : "Enter email code"} className="h-10 flex-1" data-testid={`office-email-code-${idx}`} />
                        <Button type="button" className="h-10 bg-[#004030] text-white rounded-full" onClick={() => checkEmailOtp(idx, e)} data-testid={`office-email-confirm-${idx}`}><Check size={15} /></Button>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label>Tel (optional)</Label>
                      <div className="flex gap-1">
                        <Select value={e.tel_dial} onValueChange={(v) => setEntry(idx, { tel_dial: v })}>
                          <SelectTrigger className="h-11 w-24"><SelectValue /></SelectTrigger>
                          <SelectContent>{dial.map((d, i) => <SelectItem key={`t${d.code}${i}`} value={d.dial}>{d.flag} {d.dial}</SelectItem>)}</SelectContent>
                        </Select>
                        <Input value={e.tel} onChange={(ev) => setEntry(idx, { tel: ev.target.value })} className="h-11 flex-1" data-testid={`office-tel-${idx}`} />
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label>Mobile <span className="text-red-500">*</span> {e.mobile_verified && <span className="text-green-600 text-xs">✓</span>}</Label>
                      <div className="flex gap-1">
                        <Select value={e.mobile_dial} onValueChange={(v) => setEntry(idx, { mobile_dial: v })} disabled={e.mobile_verified}>
                          <SelectTrigger className="h-11 w-24"><SelectValue /></SelectTrigger>
                          <SelectContent>{dial.map((d, i) => <SelectItem key={`m${d.code}${i}`} value={d.dial}>{d.flag} {d.dial}</SelectItem>)}</SelectContent>
                        </Select>
                        <Input value={e.mobile} disabled={e.mobile_verified} onChange={(ev) => setEntry(idx, { mobile: ev.target.value, mobile_verified: false })} className={`h-11 flex-1 ${e.mobile && !phoneValid(e.mobile) ? "border-red-400" : ""}`} data-testid={`office-mobile-${idx}`} />
                        {!e.mobile_verified && <Button type="button" variant="outline" className="h-11 rounded-full whitespace-nowrap" onClick={() => sendMobileOtp(idx, e)} data-testid={`office-mobile-verify-${idx}`}>Verify</Button>}
                      </div>
                      {e._mobileSent && !e.mobile_verified && (
                        <div className="flex gap-2 mt-1">
                          <Input value={e._mobileCode} onChange={(ev) => setEntry(idx, { _mobileCode: ev.target.value })} placeholder={e._mobileDemo ? `code (demo ${e._mobileDemo})` : "Enter SMS code"} className="h-10 flex-1" data-testid={`office-mobile-code-${idx}`} />
                          <Button type="button" className="h-10 bg-[#004030] text-white rounded-full" onClick={() => checkMobileOtp(idx, e)} data-testid={`office-mobile-confirm-${idx}`}><Check size={15} /></Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}

            <Button type="button" variant="outline" onClick={addEntry} className="w-full rounded-full border-dashed" data-testid="add-another-office"><Plus size={15} className="mr-1" /> Add office</Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[#004030] hover:bg-[#013d2b] text-white rounded-full" data-testid="office-save">{saving ? <Loader2 size={16} className="animate-spin" /> : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
